import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Folder, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import TopBar from '../components/TopBar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import VoterInflationData from '../components/VoterInflationData';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATIC_HEADER = { cta_url: "/donate" };
const STATIC_FOOTER = { contact_email: "info@securethevotemd.com" };

const SpotlightSeriesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '1');
  const [data, setData] = useState({ posts: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearch, setActiveSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set('page', currentPage);
    params.set('limit', '3');
    params.set('category', 'spotlight-series');
    if (activeSearch) params.set('search', activeSearch);
    axios.get(`${API_URL}/api/posts/list?${params}`)
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [currentPage, activeSearch]);

  const handleSearch = (e) => {
    e.preventDefault();
    setActiveSearch(searchQuery);
    setSearchParams({});
  };

  const clearSearch = () => {
    setSearchQuery('');
    setActiveSearch('');
    setSearchParams({});
  };

  const setPage = (p) => {
    setSearchParams({ page: p });
    window.scrollTo({ top: document.getElementById('spotlight-posts')?.offsetTop - 100, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="spotlight-series-page">
      <TopBar />
      <Header config={STATIC_HEADER} />

      {/* Hero */}
      <div className="relative pt-[50px]">
        <div className="absolute inset-0" style={{ backgroundColor: '#9E1B32' }} />
        <div className="absolute inset-0" style={{
          backgroundImage: "url('https://securethevotemd.com/images/2025/03/2025-Feb-25-Secure-the-Vorte-66-1024x683.jpg')",
          backgroundSize: 'cover', backgroundPosition: 'center'
        }} />
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(158, 27, 50, 0.75)' }} />
        <div className="relative z-10 py-20 sm:py-28">
          <div className="container mx-auto px-4 max-w-5xl text-center">
            <motion.h1
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Spotlight Series
            </motion.h1>
            <motion.div
              className="w-16 h-1 bg-[#F6BF58] mx-auto mt-6"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.3 }}
            />
          </div>
        </div>
      </div>

      <main className="py-12 sm:py-16">
        <div className="container mx-auto px-4 max-w-5xl">

          {/* Logo + Description */}
          <motion.div
            className="flex flex-col md:flex-row items-center gap-8 mb-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="md:w-1/3 shrink-0">
              <img
                src={`${process.env.PUBLIC_URL || ""}/images/spotlight-series/spotlight-series-1.png`}
                alt="Tales from the Data — Spotlight Series"
                className="w-full max-w-[300px] mx-auto"
              />
            </div>
            <div className="md:w-2/3">
              <p className="text-slate-700 text-lg leading-relaxed">
                The <strong>Spotlight Series</strong> highlights and showcases the state of Maryland's voter rolls through data-driven analysis. Updated weekly with new themes, this series brings transparency to the voter registration data that underpins our elections.
              </p>
            </div>
          </motion.div>

          {/* Disclaimer */}
          <motion.div
            className="bg-slate-50 rounded-lg p-6 mb-12"
            style={{ borderLeft: '4px solid #9E1B32' }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h3 className="font-bold text-slate-900 mb-3 text-sm uppercase tracking-wider">Data Source and Legal Authority Statement</h3>
            <div className="text-sm text-slate-600 leading-relaxed space-y-3">
              <p>Secure the Vote Maryland (SecuretheVoteMD) obtains official Maryland voter registration data directly from the State, as permitted under Maryland law. Specifically, Maryland Election Law &sect; 3-506 authorizes the State Board of Elections to provide copies of the statewide voter registration list to applicants, subject to certain conditions and permitted uses.</p>
              <p>The data used in our analysis is therefore drawn from the same official voter registration records maintained and relied upon by election officials to administer elections and conduct voter list maintenance.</p>
              <p>Our work does not rely on speculation or unofficial sources. Rather, we organize, review, and cross-reference this official voter file with other publicly available records (such as death records and address data) to identify entries that may warrant further review.</p>
              <p>Accordingly, the information presented in our reports is based on actual data contained within the State's voter rolls. These findings are not allegations, but data-driven observations derived from official records and supported by reasonable verification methods.</p>
              <p>SecuretheVoteMD does not make final determinations regarding voter eligibility. All conclusions and actions remain exclusively within the authority of election officials, in accordance with applicable law.</p>
            </div>
          </motion.div>

          {/* Search */}
          <div className="mb-8" id="spotlight-posts">
            <form onSubmit={handleSearch} className="flex gap-2 max-w-lg mx-auto">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search spotlight series..."
                  className="pl-9 h-10"
                  data-testid="spotlight-search"
                />
              </div>
              <Button type="submit" size="sm" className="h-10 px-5" style={{ backgroundColor: '#9E1B32' }}>Search</Button>
              {activeSearch && (
                <Button type="button" variant="outline" size="sm" className="h-10" onClick={clearSearch}>Clear</Button>
              )}
            </form>
            {activeSearch && (
              <p className="text-center text-sm text-slate-500 mt-2">
                Results for "<span className="font-medium text-slate-700">{activeSearch}</span>" &mdash; {data.total} found
              </p>
            )}
          </div>

          {/* Blog Cards */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[0,1,2].map(i => (
                <div key={i} className="bg-white rounded-lg overflow-hidden shadow-md">
                  <div className="h-48 bg-slate-100 animate-pulse" />
                  <div className="p-6 space-y-3">
                    <div className="h-4 bg-slate-100 rounded animate-pulse w-1/3" />
                    <div className="h-5 bg-slate-100 rounded animate-pulse w-full" />
                    <div className="h-4 bg-slate-100 rounded animate-pulse w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : data.posts.length === 0 ? (
            <div className="text-center py-12 text-slate-500">No spotlight articles found.</div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {data.posts.map((post, index) => {
                  const date = post.published_at ? new Date(post.published_at) : null;
                  return (
                    <motion.article
                      key={post.id}
                      className="bg-white rounded-lg overflow-hidden shadow-md group"
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <Link to={`/blog/${post.slug}`}>
                        <div className="relative overflow-hidden">
                          {post.featured_image ? (
                            <img src={post.featured_image} alt={post.title} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500" />
                          ) : (
                            <div className="w-full h-48 bg-gradient-to-br from-[#9E1B32] to-[#6B1029] flex items-center justify-center">
                              <span className="text-white/30 text-6xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>STV</span>
                            </div>
                          )}
                          {date && (
                            <div className="absolute bottom-0 left-0 bg-[#9E1B32] text-white px-4 py-2">
                              <span className="block text-xl font-bold">{date.toLocaleDateString('en-US', { day: '2-digit' })}</span>
                              <span className="text-sm">{date.toLocaleDateString('en-US', { month: 'short' })}</span>
                            </div>
                          )}
                        </div>
                        <div className="p-6">
                          <div className="flex items-center gap-2 text-slate-500 text-sm mb-3">
                            <Folder size={14} />
                            <span>Spotlight Series</span>
                          </div>
                          <h3 className="text-lg font-bold text-slate-900 group-hover:text-[#9E1B32] transition-colors mb-3 line-clamp-2">{post.title}</h3>
                          {post.excerpt && <p className="text-slate-600 text-sm line-clamp-2 mb-4">{post.excerpt}</p>}
                          <span className="inline-flex items-center gap-2 text-[#9E1B32] font-semibold text-sm group-hover:gap-3 transition-all duration-300">
                            READ MORE <ArrowRight size={14} />
                          </span>
                        </div>
                      </Link>
                    </motion.article>
                  );
                })}
              </div>

              {/* Pagination */}
              {data.pages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-10">
                  <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}>
                    <ChevronLeft size={16} />
                  </Button>
                  {Array.from({ length: Math.min(data.pages, 7) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <Button
                        key={pageNum}
                        variant={pageNum === currentPage ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setPage(pageNum)}
                        style={pageNum === currentPage ? { backgroundColor: '#9E1B32' } : {}}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  <Button variant="outline" size="sm" disabled={currentPage >= data.pages} onClick={() => setPage(currentPage + 1)}>
                    <ChevronRight size={16} />
                  </Button>
                </div>
              )}
              <div className="text-center text-sm text-slate-500 mt-3">
                Showing {(currentPage - 1) * 3 + 1}&ndash;{Math.min(currentPage * 3, data.total)} of {data.total}
              </div>
            </>
          )}
        </div>

        <div className="container mx-auto px-4 max-w-5xl mt-16 space-y-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            <img
              src={`${process.env.PUBLIC_URL || ""}/images/spotlight-series/spotlight-series-1.png`}
              alt="Spotlight Series graphic"
              className="w-full h-auto"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            <img
              src={`${process.env.PUBLIC_URL || ""}/images/spotlight-series/spotlight-deceased-voter-registrations-2026.jpg`}
              alt="Spotlight Series graph showing deceased voter registrations data"
              className="w-full h-auto rounded-lg shadow-md"
            />
          </motion.div>
        </div>

        {/* Prior Content — Voter Registration Inflation Data */}
        <div className="container mx-auto px-4 max-w-5xl mt-16 pb-4">
          <motion.hr
            className="border-slate-200 mb-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          />
          <VoterInflationData />
        </div>
      </main>

      <Footer config={STATIC_FOOTER} />
    </div>
  );
};

export default SpotlightSeriesPage;

